// @ts-ignore

import { Component, ViewEncapsulation, Input, forwardRef, ViewChild, ElementRef, OnInit, Output, EventEmitter } from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseControlValueAccessor } from '../../models/forms/base-control-value-accessor';
import { UtilitiesService } from '../../services/utilities.service';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {StringService} from "../../services/string.service";

@Component({
    selector: 'textbox',
    templateUrl: './textbox.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextboxComponent),
            multi: true
        }
    ]
})

export class TextboxComponent extends BaseControlValueAccessor<string> implements OnInit {
    private _maxLength: number = null;
    private _minLength: number = null;
    private _autofocus: boolean = false;
    private _step: number = null;
    private _min: number = null;

    @Input() id: string = null;
    // input type helps validation: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
    @Input() type: string = 'text';
    @Input() cssClasses: string = null;
    @Input() tabIndex: number;
    @Input() placeholder: string;
    @Input() prepend: string;
    @Input() small: boolean;

    // This allows a browser to display an appropriate virtual keyboard: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
    @Input() inputmode: "none" | "text" | "decimal" | "numeric" | "tel" | "search" | "email" | "url" = "text";    // text is default

    //Manage error validation on input without waiting for form submission eg. organisation-supplier-add-form.component.html
    @Input() invalidMessage: string = '';
    @Input() encodeValue: boolean = false;

    isInputFocused: boolean = false;

    @ViewChild('append', {static: true}) append: ElementRef;

    @Input() set autofocus(value: any) {
        this._autofocus = coerceBooleanProperty(value);
        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (this._autofocus) {
                setTimeout(() => this.textboxElement.nativeElement.focus());
            }
        }
    }

    get readonly() {
        return this._readonly;
    }

    @Input() set readonly(value: boolean) {
        this._readonly = value;

        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (value) {
                this.textboxElement.nativeElement.readOnly = true;
            } else {
                this.textboxElement.nativeElement.removeAttribute('readOnly');
            }
        }
    }

    get disabled() {
        return this._disabled;
    }

    @Input() set disabled(value: boolean) {
        this._disabled = value;

        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (value) {
                this.textboxElement.nativeElement.disabled = true;
            } else {
                this.textboxElement.nativeElement.removeAttribute('disabled');
            }
        }
    }

    @Input() set minLength(value: number) {
        this._minLength = value;

        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (value) {
                this.textboxElement.nativeElement.minLength = value;
            } else {
                this.textboxElement.nativeElement.removeAttribute('minLength');
            }
        }
    }

    @Input() set maxLength(value: number) {
        this._maxLength = value;

        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (value) {
                this.textboxElement.nativeElement.maxLength = value;
            } else {
                this.textboxElement.nativeElement.removeAttribute('maxLength');
            }
        }
    }

    @Input() set step(value: number) {
        this._step = value;

        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (value) {
                this.textboxElement.nativeElement.step = value;
            } else {
                this.textboxElement.nativeElement.removeAttribute('step');
            }
        }
    }

    @Input() set min(value: number) {
        this._min = value;

        if (this.textboxElement && this.textboxElement.nativeElement) {
            if (value) {
                this.textboxElement.nativeElement.min = value;
            } else {
                this.textboxElement.nativeElement.removeAttribute('min');
            }
        }
    }

    @Output('keydown') keydown = new EventEmitter<KeyboardEvent>();
    @Output('keyup') keyup = new EventEmitter<KeyboardEvent>();
    @Output() onBlur = new EventEmitter<void>();
    @Output() onEnter = new EventEmitter<void>();
    @Output() enterPressed = new EventEmitter<void>();

    @ViewChild('textbox', {static: true}) textboxElement: ElementRef;
    @ViewChild('append', {static: true}) header: ElementRef;

    constructor(private element: ElementRef, protected controlContainer: ControlContainer, protected utilitiesService: UtilitiesService, private stringService: StringService) {
        super(controlContainer, utilitiesService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        if (this.element.nativeElement.id) {
            this.element.nativeElement.removeAttribute('id');
        }

        this.disabled = this._disabled;
        this.readonly = this._readonly;
        this.maxLength = this._maxLength;
        this.autofocus = this._autofocus;
        this.step = this._step;
        this.min = this._min;
    }

    ngAfterViewInit() {
        this.textboxElement.nativeElement.addEventListener('focus', () => {
            this.isInputFocused = true;
        });

        this.textboxElement.nativeElement.addEventListener('blur', () => {
            this.isInputFocused = false;
            this.onBlur.emit();
        });
    }

    internalOnChange(event) {
        let value = event.target.value;
        if (this.encodeValue) {
            value = this.stringService.htmlEncode(value);
        }
        this.onChange(value);
    }

    internalKeyUp(event: KeyboardEvent) {
        this.internalOnChange(event);
        this.keyup.emit(event);

        if (event.key === "Enter") {
            this.onEnter.emit();
        }
    }

    internalKeyDown(event: KeyboardEvent) {
        this.keydown.emit(event);
        if(event.key === 'Enter'){
            this.enterPressed.emit();
        }
    }

    focus() {
        setTimeout(() => this.textboxElement.nativeElement.focus());
    }
}
